"use client";

import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { GroupeEgogramme } from "@repo/utils";
import { couleurFamille, couleurSousGroupe } from "./egogrammePalette";

/**
 * Une barre de l'histogramme : un état du moi, déjà ordonné par l'appelant
 * (`ordonnerGroupesEgogramme`). Forme MINIMALE et locale — ce paquet ne dépend
 * pas de Convex, seulement du type de score de `@repo/utils`.
 */
export type BarreEgogramme = [nom: string, groupe: GroupeEgogramme];

type Props = {
  /** Groupes DANS L'ORDRE d'affichage. Le composant ne retrie pas. */
  groupes: BarreEgogramme[];
  hauteur?: string;
};

/**
 * Lit un token du design system. ECharts veut des chaînes de couleur, pas des
 * classes : sans ce détour, un rebrand devrait repasser dans les graphes.
 */
function jeton(nom: string, repli: string): string {
  if (typeof window === "undefined") return repli;
  const valeur = getComputedStyle(document.documentElement)
    .getPropertyValue(nom)
    .trim();
  return valeur || repli;
}

/** Taux d'accord 0-100. Un groupe sans réponse vaut 0 et non NaN. */
const taux = (g: GroupeEgogramme) =>
  g.total === 0 ? 0 : (g.score / g.total) * 100;

const pourcent = (n: number) => `${Math.round(n)} %`;

/**
 * Histogramme des cinq états du moi.
 *
 * **La hauteur code le TAUX d'accord, pas le score brut.** Les cinq états ne
 * portent pas le même nombre de questions (ADULTE 44, ENFANT LIBRE 20) : des
 * barres en score brut feraient lire « Adulte deux fois plus fort qu'Enfant
 * libre » là où le questionnaire ne fait que poser deux fois plus de questions.
 * Le score brut n'est pas perdu pour autant — il est écrit en clair au sommet
 * de chaque barre (`14 / 24`) et repris dans l'infobulle, sous-groupes compris.
 *
 * **Une teinte par famille**, celle de `egogrammePalette` — la même que sur les
 * cartes et sur les pastilles de la liste des réponses : une couleur désigne le
 * même état du moi partout dans la page.
 *
 * La couleur y est un RAPPEL, jamais le porteur de l'information : l'axe nomme
 * chaque famille et chaque barre écrit son score. Il le faut, car ces pastels
 * passent sous 3:1 de contraste sur la surface claire, et la paire rouge / vert
 * des deux familles Parent est indiscernable en deutéranopie. Sans ces deux
 * relais, la figure serait illisible pour une partie des lecteurs.
 *
 * L'échelle est toujours bornée à 0-100 : sans borne fixe, deux passations ne
 * se compareraient pas d'un écran à l'autre.
 */
export function EgogrammeChart({ groupes, hauteur = "320px" }: Props) {
  const option = useMemo(() => {
    const encre = jeton("--encre", "#2b1b2f");
    const axe = jeton("--chart-axe", "#334155");

    return {
      animation: false,
      grid: { left: 8, right: 16, top: 28, bottom: 8, containLabel: true },
      tooltip: {
        trigger: "item",
        formatter: (params: { dataIndex: number }) => {
          const entree = groupes[params.dataIndex];
          if (!entree) return "";
          const [nom, groupe] = entree;
          const detail = Object.entries(groupe.sousGroupes ?? {})
            .map(([sousNom, s]) => {
              const pastille =
                `<span style="display:inline-block;width:8px;height:8px;` +
                `border-radius:2px;margin-right:6px;` +
                `background:${couleurSousGroupe(nom, sousNom)}"></span>`;
              return `<div style="opacity:.85">${pastille}${sousNom} : ${s.score} / ${s.total}</div>`;
            })
            .join("");
          return (
            `<strong>${nom}</strong><br/>` +
            `${groupe.score} / ${groupe.total} — ${pourcent(taux(groupe))}` +
            (detail ? `<div style="margin-top:4px">${detail}</div>` : "")
          );
        },
      },
      xAxis: {
        type: "category",
        data: groupes.map(([nom]) => nom.replace(" ", "\n")),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: axe, opacity: 0.25 } },
        axisLabel: { color: axe, fontSize: 11, lineHeight: 14 },
      },
      yAxis: {
        type: "value",
        // Borne fixe : le taux est une part, il se lit sur 0-100 ou il ne se
        // compare pas d'une passation à l'autre.
        min: 0,
        max: 100,
        axisLabel: { color: axe, fontSize: 11, formatter: "{value} %" },
        splitLine: { lineStyle: { color: axe, opacity: 0.15 } },
      },
      series: [
        {
          type: "bar",
          // Barres larges et resserrées : cinq catégories seulement, et la
          // largeur ne code rien — la laisser filer donne des aplats de couleur
          // plus lisibles, sur lesquels l'étiquette de score se pose bien. Le
          // plafond n'existe que pour éviter des pavés sur un écran très large.
          barCategoryGap: "12%",
          barMaxWidth: 160,
          itemStyle: {
            // Extrémité arrondie côté donnée seulement : la base reste
            // franchement posée sur la ligne de zéro.
            borderRadius: [4, 4, 0, 0],
            // Liseré couleur surface : deux barres voisines ne se touchent
            // jamais, même quand leurs teintes se ressemblent.
            borderColor: jeton("--surface", "#fdf8f9"),
            borderWidth: 2,
          },
          label: {
            show: true,
            position: "top",
            // Le texte porte un jeton d'encre, jamais la couleur de la série.
            color: encre,
            fontSize: 11,
            formatter: (params: { dataIndex: number }) => {
              const groupe = groupes[params.dataIndex]?.[1];
              return groupe ? `${groupe.score} / ${groupe.total}` : "";
            },
          },
          data: groupes.map(([nom, groupe]) => ({
            value: taux(groupe),
            itemStyle: { color: couleurFamille(nom) },
          })),
        },
      ],
    };
  }, [groupes]);

  if (groupes.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        Aucune réponse exploitable : rien à représenter.
      </p>
    );
  }

  return (
    <ReactECharts
      option={option}
      notMerge
      style={{ width: "100%", height: hauteur }}
    />
  );
}
